<template>
	<article
		class="section section--expanded section--floating section--full service">
		<div class="section">
			<header>
				<div class="flex gap-2 content-between">
					<div>
						<div class="flex gap-2 content-between">
							<div>
								<h3 class="beta" v-html="lead.service.names.singular + '  Lead'"/>
								<dl class="grid-list mt-1">
									<dt class="heavy">Created</dt>
									<dd v-html="$present('dateTime', lead.created_at)"/>
								</dl>
							</div>
						</div>
					</div>
					<div class="flex flex-col gap-1 items-end">
						<div class="flex gap-1 items-center">
							<dl v-if="lead.quote">
								<dt class="sr-only">Quote</dt>
								<dd class="beta heavy" v-html="$present('price', lead.quote.calculation.total)"/>
							</dl>
							<status-badge :status="lead.status" group="lead"/>
							<v-contextual :processing="processing">
								<ul v-if="$can('update', lead)">
									<v-contextual-option
										v-if="$can('appointForRecipient', lead)"
										@click="showCalendar(lead)">Arrange Appointment</v-contextual-option>
								</ul>
								<ul>
									<v-contextual-option
										v-if="$can('qualify', lead)"
										@click="showQualifyModal(lead)">Qualify</v-contextual-option>
									<v-contextual-option
										v-if="$can('instruct', lead)"
										@click="showInstructModal(lead)">Convert to Case</v-contextual-option>
									<v-contextual-option
										v-if="$can('setAsPotential', lead)"
										@click="showSetAsPotentialModal(lead)">Set as Potential</v-contextual-option>
									<v-contextual-option
										v-if="$can('close', lead)"
										@click="showCloseModal(lead)">Close</v-contextual-option>
								</ul>
								<ul>
									<v-contextual-option
										v-if="$can('assign', lead)"
										@click="showAssignModal(lead)">Assign</v-contextual-option>
								</ul>
								<ul>
									<v-contextual-option
										v-if="$can('factFind', lead)"
										@click="showFactFindModal(lead)">Update Fact Find</v-contextual-option>
									<v-contextual-option
										v-if="$can('viewFactFindResponse', lead)"
										@click="viewFactFindResponse(lead)">View Fact Find</v-contextual-option>
								</ul>
								<ul v-if="$can('view', lead.quote)">
									<v-contextual-option
										@click="showQuoteModal(lead.service, lead.quote)">View Quote</v-contextual-option>
									<v-contextual-option
										@click="viewFile(lead.quote.pdf)">View Quote PDF</v-contextual-option>
									<v-contextual-option
										v-if="$can('update', lead.quote)"
										:route-to="{name: 'quotes.edit', params: {quote: lead.quote.id}}">Edit Quote</v-contextual-option>
									<template v-if="$can('sendQuoteEmail', lead)">
										<v-contextual-option
											@click.prevent="displayQuoteLinkModal()">Copy Quote Link</v-contextual-option>
										<v-contextual-option
											@click.prevent="displayQuoteLinkModal(true)">Send Quote Email</v-contextual-option>
									</template>
								</ul>
							</v-contextual>
						</div>
						<app-alert :alerts="lead.alerts" title="Lead Health Status"/>
					</div>
				</div>
				<div class="section section--flat mt-1">
					<dl class="flex gap-2">
						<div>
							<dt class="all-caps gamma">Assigned Team</dt>
							<dd v-if="!lead.assigned_team" class="beta">—</dd>
							<dd v-else class="beta" v-html="lead.assigned_team.name"/>
						</div>
						<div>
							<dt class="all-caps gamma">Assigned User</dt>
							<dd v-if="!lead.assigned_user" class="beta">—</dd>
							<dd v-else class="beta" v-html="$present('fullName', lead.assigned_user.profile)"/>
						</div>
					</dl>
				</div>
			</header>
			<dl v-if="lead.address" class="flex content-between gap-2 mt-1">
				<div>
					<dt class="sr-only">Address</dt>
					<dd>
						<Address
							:address="lead.address"
							:display-county="false"
							:display-locality="false"/>
					</dd>
				</div>
			</dl>
		</div>
		<section
			v-if="lead.service.key === 'conveyancing'" class="section section--bordered">
			<header class="flex gap-2 content-between">
				<h2 class="beta">Transactions</h2>
			</header>
			<div class="sections mt-2">
				<article
					v-for="(transaction, transactionIndex) in lead.quote.service_requirements"
					v-if="lead.quote.service_requirements.quote_type.includes(transactionIndex)"
					:key="transactionIndex"
					class="section section--flexed section--flat service">
					<header class="flex gap-2 content-between items-center">
						<h3 v-if="lead.address">{{ $present("ucWords", transactionIndex) }} of {{ lead.address.address_postcode }}</h3>
						<h3 v-else>{{ $present("ucWords", transactionIndex) }}</h3>
						<!--						<dl class="flex gap-1">-->
						<!--							<div>-->
						<!--								<dt class="sr-only">Quote</dt>-->
						<!--								<dd-->
						<!--									class="beta heavy"-->
						<!--									v-html="$present('price', lead.quote.calculation[transactionIndex].total)"/>-->
						<!--							</div>-->
						<!--							<div>-->
						<!--								<dt class="sr-only">Full Quote</dt>-->
						<!--								<dd>-->
						<!--									<button-->
						<!--										class="button button&#45;&#45;primary&#45;&#45;small"-->
						<!--										@click.prevent="showQuoteModal(lead.quote)">View Quote-->
						<!--									</button>-->
						<!--								</dd>-->
						<!--							</div>-->
						<!--						</dl>-->
					</header>
					<dl class="flex gap-2 mt-2">
						<div>
							<dt class="all-caps gamma">Property Value</dt>
							<dd
								class="alpha"
								v-html="$present('price', transaction.property_price)"/>
						</div>
						<div>
							<dt class="all-caps gamma mb-1">Leasehold</dt>
							<dd>
								<bool-icon :value="transaction.leasehold"/>
							</dd>
						</div>
						<div v-if="transactionIndex === 'purchase' && lead.quote.calculation.landTaxCalculation">
							<dt class="all-caps gamma">{{ lead.quote.calculation.landTaxCalculation.typeAbbreviation }} Estimation</dt>
							<dd
								class="alpha"
								v-html="lead.quote.calculation.landTaxCalculation.tax.formatted"/>
						</div>
					</dl>
				</article>
			</div>
		</section>
		<section class="section section--bordered">
			<header>
				<h2 class="beta">Meta</h2>
			</header>
			<dl class="details-list mt-2">
				<div v-for="(meta, metaIndex) in lead.meta" class="detail-list__item">
					<dt v-html="meta.key"/>
					<dd v-html="meta.value"/>
				</div>
			</dl>
		</section>
	</article>
</template>
<script>
import Address from "../../../../../components/Address.vue";
import QuoteBreakdownConveyancing from "../../../../../components/QuoteBreakdownConveyancing.vue";
import QuoteBreakdownSdlt from "../../../../../components/QuoteBreakdownSdlt.vue";
import opensFile from "../../../../../mixins/opensFile";

export default {
	components: {
		Address,
		QuoteBreakdownConveyancing,
		QuoteBreakdownSdlt,
	},

	mixins: [
		opensFile,
	],

	data() {
		return {
			processing: false,
		};
	},

	props: {
		lead: {
			required: true,
			type: Object,
		},
		client: {
			required: true,
			type: Object,
		},
		goToTab: {
			required: true,
			type: Function,
		},
	},

	methods: {
		requires(requirement) {
			return _.has(this.lead.requirements, requirement);
		},
		showCalendar() {
			this.$vmodal.open({
				component: require("../../../../../components/calendars/resource-timeline/v-resource-timeline.vue").default,
				props: {
					input: this,
					client: this.activeItem,
					lead: this.lead,
					minDate: this.minDate,
					maxDate: this.maxDate,
				},
			});
		},
		showFactFindModal(lead) {
			this.$vmodal.open({
				component: require("./modals/EditFactFile.vue").default,
				props: {
					lead: lead,
				},
			});
		},
		viewFactFindResponse() {
			this.$vmodal.open({
				component: require("../../../../../components/modals/v-fact-find-response-modal.vue").default,
				props: {
					structure: this.lead.factFindResponse.structure,
					data: this.lead.factFindResponse.data,
				},
			});
		},
		showAssignModal() {
			this.$vmodal.open({
				component: require("./modals/Assign.vue").default,
				props: {
					lead: this.lead,
				},
			});
		},
		showCloseModal() {
			this.$vmodal.open({
				component: require("./modals/Close.vue").default,
				props: {
					lead: this.lead,
				},
			});
		},
		showQualifyModal() {
			this.$vmodal.open({
				component: require("./modals/Qualify.vue").default,
				props: {
					lead: this.lead,
					success: () => {
						// this.$nextTick(() => {
						// 	this.refs.tabs.goToTab("instructions");
						// });
					},
				},
			});
		},
		showInstructModal() {
			this.$vmodal.open({
				component: () => import("./modals/Instruct.vue"),
				props: {
					lead: this.lead,
					client: this.client,
					success: () => {
						this.$nextTick(() => {
							this.goToTab("instructions");
						});
					},
				},
			});
		},
		showSetAsPotentialModal() {
			this.$vmodal.open({
				component: () => import("./modals/SetAsPotential.vue"),
				props: {
					lead: this.lead,
				},
			});
		},
		showQuoteModal(service, quote) {
			let component = {
				conveyancing: QuoteBreakdownConveyancing,
				landTaxCalculation: QuoteBreakdownSdlt,
			}[service.key];
			this.$vmodal.open({
				component: component,
				props: {
					quote: quote,
				},
			});
		},
		displayQuoteLinkModal(sendEmail = false) {
			this.$vmodal.open({
				component: () => import("../modals/QuoteLink.vue"),
				props: {
					lead: this.lead,
					client: this.client,
					shouldSendEmail: sendEmail,
				},
			});
		},
	},
};
</script>
